import React, {
  type Dispatch,
  memo,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type ScrollView, StyleSheet, View } from 'react-native';
import { type DietaryProperty } from '@sg/graphql-schema';

import { CustomizationSelectedIngredients } from '@order/features/customization';
import { type IngredientModificationType } from '@order/utils';

import { useDietaryRestrictions } from '../../../../../../components';
import { INGREDIENT_IMAGE_WIDTH } from '../../../../constants';
import { useFilteredIngredientsModifications } from '../../../../hooks';
import { type useProductDetailsScreenContext } from '../../../../ProductDetailsScreen.provider';
import {
  checkIfDressingKind,
  getActiveIngredientsModifications,
} from '../../../../state';
import type { IngredientModificationWithQuantity } from '../../../../types';
import {
  getFormattedQuantityByKind,
  useSectionHelperText,
} from '../../helpers';
import { AddModificationCta } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientsReviewPanel = memo(
  (props: IngredientsReviewPanelProps) => {
    const {
      product,
      modifications,
      addIngredientModification,
      removeIngredientModification,
      completelyRemoveIngredientModification,
      setActiveTabItemId,
      setDefaultActiveTargetId,
    } = props;

    const getSectionHelperText = useSectionHelperText();

    const ingredientModifications = product?.ingredientModifications;
    const { active } = modifications;

    const getRestrictionNotice = useDietaryRestrictions({ referrer: 'ProductDetails' }); // prettier-ignore

    // ─── Refs ────────────────────────────────────────────────────────────

    const detailsContainerRef = useRef<ScrollView>(null);

    // ─── State ───────────────────────────────────────────────────────────

    const [activeIngredientId, setActiveIngredientId] = useState<string>();

    const clearActiveIngredientId = useCallback(() => {
      setActiveIngredientId(undefined);
    }, []);

    // ─── Data ────────────────────────────────────────────────────────────

    const activeIngredientModifications = useMemo(
      () => getActiveIngredientsModifications(modifications),
      [modifications],
    );

    const { ingredientsModificationsKinds, ingredientsModifications } =
      useFilteredIngredientsModifications(ingredientModifications);

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View style={styles.reviewPanelContainer}>
        <CustomizationSelectedIngredients.Container>
          <CustomizationSelectedIngredients.List>
            {ingredientsModificationsKinds.map((ingredientKindName, index) => {
              const formattedQuantity = getFormattedQuantityByKind({
                active,
                ingredientsModifications,
                ingredientKindName,
              });

              const isLastSection =
                index === ingredientsModificationsKinds.length - 1;

              const ingredientsForThisSection =
                activeIngredientModifications.filter(
                  (ingredientModification) =>
                    ingredientModification.kind === ingredientKindName,
                );

              const helperText = getSectionHelperText(
                ingredientsModifications,
                ingredientKindName,
              );

              return (
                <CustomizationSelectedIngredients.IngredientKindSection
                  key={ingredientKindName}
                  ingredientKindName={ingredientKindName}
                  helperText={helperText}
                  style={isLastSection ? styles.withoutBorder : null}
                  formattedQuantity={formattedQuantity}
                >
                  {ingredientsForThisSection.map((ingredientModification) => {
                    const isActive =
                      activeIngredientId ===
                      ingredientModification.ingredient.id;

                    const isDressing = checkIfDressingKind(
                      ingredientModification.kind,
                    );

                    const restrictionNotice = getRestrictionNotice(
                      ingredientModification?.ingredient?.dietaryProperties,
                    );

                    const dietaryRestrictions =
                      ingredientModification?.ingredient?.dietaryProperties
                        ?.map(prepareDietaryRestrictions)
                        .join(', ');

                    return (
                      <CustomizationSelectedIngredients.ListItem
                        key={ingredientModification.ingredient.id}
                        isActive={isActive}
                        shouldScrollToSelf={false}
                        scrollViewRef={detailsContainerRef}
                      >
                        <CustomizationSelectedIngredients.Ingredient<IngredientModificationWithQuantity>
                          ingredientModification={ingredientModification}
                          isActive={isActive}
                          isDisabled={isDressing}
                          restrictionNotice={restrictionNotice}
                          dietaryRestrictions={dietaryRestrictions}
                          imageWidth={INGREDIENT_IMAGE_WIDTH}
                          setActive={setActiveIngredientId}
                          setInactive={clearActiveIngredientId}
                          onRemove={completelyRemoveIngredientModification}
                          onIncreaseQuantity={addIngredientModification}
                          onDecreaseQuantity={removeIngredientModification}
                        />
                      </CustomizationSelectedIngredients.ListItem>
                    );
                  })}

                  <AddModificationCta
                    active={active}
                    ingredientsModifications={ingredientsModifications}
                    ingredientModificationType={ingredientKindName}
                    setActiveTabItemId={setActiveTabItemId}
                    setDefaultActiveTargetId={setDefaultActiveTargetId}
                  />
                </CustomizationSelectedIngredients.IngredientKindSection>
              );
            })}
          </CustomizationSelectedIngredients.List>
        </CustomizationSelectedIngredients.Container>
      </View>
    );
  },
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const prepareDietaryRestrictions = (dietaryProperty: DietaryProperty) => {
  return dietaryProperty.name.toLowerCase();
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  reviewPanelContainer: {
    flex: 1,
  },
  withoutBorder: {
    borderBottomWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type PDPContext = ReturnType<typeof useProductDetailsScreenContext>;

type IngredientsReviewPanelProps = {
  product: PDPContext['product'];
  modifications: PDPContext['modifications'];
  addIngredientModification: PDPContext['addIngredientModification'];
  removeIngredientModification: PDPContext['removeIngredientModification'];
  completelyRemoveIngredientModification: PDPContext['completelyRemoveIngredientModification'];
  setActiveTabItemId: Dispatch<SetStateAction<string>>;
  setDefaultActiveTargetId: (section: IngredientModificationType) => void;
};
